import { HTML5Video, YouTubeVideo } from './partials';
import type { VideoProps } from './types';

/**
 * Video component - renders HTML5 or YouTube videos with lazy loading and progressive enhancement.
 *
 * Automatically routes to the appropriate video component:
 * - YouTubeVideo: When video.youtubeId is present
 * - HTML5Video: For standard video files (mp4, webm)
 *
 * Features:
 * - Lazy loading: Videos load when near viewport (200px margin)
 * - Progressive enhancement: Above-the-fold HTML5 videos work without JavaScript
 * - Performance optimized: Defers loading until visible
 * - Autoplay support: HTML5 videos conditionally autoplay based on visibility
 * - Schema.org: Conditionally renders VideoObject structured data for SEO
 *
 * @param {VideoProps} props - Component props
 * @param {VideoNode} props.video - Video configuration (sources/src, autoPlay, loop, thumbnailUrl, youtubeId, schemaData)
 * @param {boolean} [props.loadInstantly=false] - If true, renders immediately (for above-the-fold videos)
 * @param {boolean} [props.renderSchema=true] - If true, renders Schema.org VideoObject inline. Set to false for KeyVisual (schema in page head)
 *
 * @example
 * // Magazine: HTML5 video with inline schema (default)
 * <Video video={{ sources: [...], autoPlay: true, schemaData: {...} }} loadInstantly={false} />
 *
 * @example
 * // KeyVisual: YouTube video without inline schema (schema rendered at page-level)
 * <Video video={{ youtubeId: 'dQw4w9WgXcQ', src: 'https://...' }} loadInstantly={true} renderSchema={false} />
 */
export function Video(props: VideoProps) {
  const {
    video: { youtubeId, schemaData, caption },
    renderSchema = true,
  } = props;
  const hasYouTubeVideo = !!youtubeId;

  return (
    <>
      {hasYouTubeVideo ? <YouTubeVideo {...props} /> : <HTML5Video {...props} />}
      {renderSchema && schemaData && (
        <script
          id={caption}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify({ ...schemaData, '@context': 'https://schema.org' }) }}
        />
      )}
    </>
  );
}
